import { useState } from "react";
import { FeatureCollection } from "geojson";

import { useGetVersionQuery } from "fond/api";
import { getArchitectureAddressTypes, getUnknownAddressTypes } from "fond/project/addressTypes";
import { useAppSelector } from "fond/utils/hooks";

export type AddressTypeCheck = {
  /** Address types in the selected addresses that the architecture's demand model doesn't know. */
  unknownAddressTypes: string[];
  /** Whether the selected addresses lack the address type field. */
  addressTypeFieldMissing: boolean;
  /** The selected addresses. */
  featureCollection: FeatureCollection;
  /** The field holding each address's type. */
  selectedAddressTypeField: string;
  /** Check the address types of `features`, optionally reading them from another field. */
  identifyUnknownAddressTypes(features: FeatureCollection, addressTypeField?: string): void;
  /** Select addresses and check their address types. */
  checkAddresses(features: FeatureCollection): void;
  /** Clear the check's findings. */
  reset(): void;
};

const EMPTY_FEATURE_COLLECTION: FeatureCollection = { type: "FeatureCollection", features: [] };

/**
 * Checks the address types of addresses selected for upload against the current version's architecture.
 */
export const useAddressTypeCheck = (): AddressTypeCheck => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });

  const [unknownAddressTypes, setUnknownAddressTypes] = useState<string[]>([]);
  const [addressTypeFieldMissing, setAddressTypeFieldMissing] = useState(false);
  const [featureCollection, setFeatureCollection] = useState<FeatureCollection>(EMPTY_FEATURE_COLLECTION);
  const [selectedAddressTypeField, setSelectedAddressTypeField] = useState<string>(version?.AddressTypeField || "AddressType");

  const architectureAddressTypes = version?.Architecture ? getArchitectureAddressTypes({ architecture: version.Architecture }) : null;

  const identifyUnknownAddressTypes = (features: FeatureCollection, addressTypeField?: string) => {
    if (addressTypeField) setSelectedAddressTypeField(addressTypeField);
    if (architectureAddressTypes !== null) {
      const { unknownAddressTypes: addressTypes, addressTypeFieldPresent } = getUnknownAddressTypes({
        featureCollection: features,
        knownAddressTypes: architectureAddressTypes,
        addressTypeField,
      });

      setUnknownAddressTypes(addressTypes);
      setAddressTypeFieldMissing(!addressTypeFieldPresent);
    } else {
      setUnknownAddressTypes([]);
      setAddressTypeFieldMissing(false);
    }
  };

  const checkAddresses = (features: FeatureCollection) => {
    identifyUnknownAddressTypes(features);
    setFeatureCollection(features);
  };

  const reset = () => {
    setUnknownAddressTypes([]);
    setAddressTypeFieldMissing(false);
  };

  return {
    unknownAddressTypes,
    addressTypeFieldMissing,
    featureCollection,
    selectedAddressTypeField,
    identifyUnknownAddressTypes,
    checkAddresses,
    reset,
  };
};
